import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const EXIT_MS = 220;

function ModalShell({
  children,
  onClose,
  panelClassName = "max-w-lg sm:max-w-3xl",
  closeRef,
}) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const timerRef = useRef();

  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    timerRef.current = window.setTimeout(() => {
      onCloseRef.current?.();
    }, EXIT_MS);
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timerRef.current);
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (!closeRef) return undefined;
    closeRef.current = requestClose;
    return () => {
      closeRef.current = () => {};
    };
  }, [closeRef, requestClose]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const shown = visible && !leaving;

  return (
    <>
      <div
        className={`modal-motion fixed inset-0 z-40 bg-black/25 transition-opacity duration-200 ease-out ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
        onClick={requestClose}
      >
        <div className="flex min-h-full w-full items-start justify-center px-3 py-4 sm:items-center sm:px-6 sm:py-8">
          <div
            className={`modal-motion my-auto w-full min-w-0 transition duration-200 ease-out ${panelClassName} ${
              shown
                ? "translate-y-0 scale-100 opacity-100"
                : "translate-y-3 scale-[0.98] opacity-0"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            {typeof children === "function" ? children(requestClose) : children}
          </div>
        </div>
      </div>
    </>
  );
}

ModalShell.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onClose: PropTypes.func,
  panelClassName: PropTypes.string,
  closeRef: PropTypes.shape({ current: PropTypes.func }),
};

export default ModalShell;
